import { Link, useLocation } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { SparklesIcon, UserCircleIcon, Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import { useState, useEffect } from 'react'

export default function Header() {
  const { user, isAuthenticated, logout } = useAuthStore()
  const location = useLocation()
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleLogin = () => {
    const authUrl = `https://auth.7e.ink/oauth/authorize?client_id=astro&redirect_uri=${encodeURIComponent(window.location.origin + '/auth/callback')}&response_type=code`
    window.location.href = authUrl
  }

  const navItems = [
    { path: '/', label: '首页' },
    { path: '/chart', label: '生成星盘' },
    ...(isAuthenticated ? [{ path: '/user', label: '我的星盘' }] : []),
  ]

  const isActive = (path: string) => location.pathname === path

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-cyber-black/90 backdrop-blur-xl border-b border-cyber-blue/20' 
          : 'bg-transparent'
      }`}
    >
      {/* 顶部装饰线 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyber-blue/50 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative">
              <div className="absolute inset-0 bg-cyber-blue rounded-lg blur-lg opacity-50 group-hover:opacity-80 transition-opacity" />
              <div className="relative w-10 h-10 rounded-lg bg-gradient-to-br from-cyber-blue to-cyber-purple flex items-center justify-center">
                <SparklesIcon className="w-6 h-6 text-cyber-black" />
              </div>
            </div>
            <div className="flex flex-col">
              <span className="text-lg md:text-xl font-bold text-gradient font-cyber tracking-wider">
                ASTRO
              </span>
              <span className="text-[10px] text-cyber-blue/60 -mt-1 tracking-[0.3em] font-mono">
                SYSTEM
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative px-4 py-2 text-sm font-cyber tracking-wide transition-colors ${
                  isActive(item.path)
                    ? 'text-cyber-blue'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {isActive(item.path) && (
                  <span className="absolute inset-0 bg-cyber-blue/10 rounded-lg" />
                )}
                <span className="relative">
                  {item.label}
                  {isActive(item.path) && (
                    <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-cyber-blue shadow-[0_0_10px_rgba(0,240,255,0.5)]" />
                  )}
                </span>
              </Link>
            ))}
          </nav>

          {/* User Section */}
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <div className="flex items-center gap-3">
                <Link 
                  to="/user" 
                  className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors group"
                >
                  {user?.avatar_url ? (
                    <div className="relative">
                      <div className="absolute inset-0 bg-cyber-blue rounded-full blur opacity-50 group-hover:opacity-80 transition-opacity" />
                      <img 
                        src={user.avatar_url} 
                        alt={user.display_name || ''}
                        className="relative w-8 h-8 rounded-full border-2 border-cyber-blue/50"
                      />
                      <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-cyber-green rounded-full border-2 border-cyber-black" />
                    </div>
                  ) : (
                    <div className="relative">
                      <UserCircleIcon className="w-8 h-8 text-gray-400 group-hover:text-cyber-blue transition-colors" />
                      <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-cyber-green rounded-full border-2 border-cyber-black" />
                    </div>
                  )}
                  <span className="hidden lg:block text-sm text-gray-300 group-hover:text-white">
                    {user?.display_name || user?.email?.split('@')[0]}
                  </span>
                </Link>
                <button
                  onClick={logout}
                  className="p-2 text-gray-500 hover:text-cyber-pink transition-colors rounded-lg hover:bg-cyber-pink/10"
                  title="退出登录"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </button>
              </div>
            ) : (
              <button
                onClick={handleLogin}
                className="cyber-button cyber-button-sm"
              >
                <span>登录</span>
              </button>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-gray-400 hover:text-white transition-colors"
            >
              {isMobileMenuOpen ? (
                <XMarkIcon className="w-6 h-6" />
              ) : (
                <Bars3Icon className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-cyber-black/95 backdrop-blur-xl border-b border-cyber-blue/20">
          <nav className="px-4 py-4 space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-lg font-cyber text-sm transition-colors ${
                  isActive(item.path)
                    ? 'bg-cyber-blue/20 text-cyber-blue border border-cyber-blue/30'
                    : 'text-gray-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  )
}
